import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './button';
import { Dialog, DialogContent } from './dialog';
import { 
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, X, Maximize2, 
  Grid, Rows, Play, Pause
} from 'lucide-react';

/**
 * Interactive Image Gallery/Slider Component
 * Features:
 * - Mouse drag sliding (desktop)
 * - Touch swipe gestures (mobile)
 * - Arrow navigation
 * - Thumbnail preview
 * - Smooth animations
 * - Fullscreen mode
 * - Zoom in/out
 * - Lazy loading
 * - Auto-play option
 */
export const ImageGallerySlider = ({
  images = [],
  showThumbnails = true,
  showArrows = true,
  showDots = true,
  autoPlay = false,
  autoPlayInterval = 5000,
  enableZoom = true,
  enableFullscreen = true,
  aspectRatio = '16/9', // '1/1', '4/3', '16/9', 'auto'
  className = '',
  onImageClick,
  thumbnailPosition = 'bottom' // 'bottom', 'left', 'right'
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});
  const sliderRef = useRef(null);
  const autoPlayRef = useRef(null);

  // Auto-play functionality
  useEffect(() => {
    if (isPlaying && images.length > 1) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
      }, autoPlayInterval);
    }
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isPlaying, images.length, autoPlayInterval]);

  // Lazy loading
  useEffect(() => {
    // Preload current, previous, and next images
    const indicesToLoad = [
      currentIndex,
      (currentIndex + 1) % images.length,
      (currentIndex - 1 + images.length) % images.length
    ];
    
    indicesToLoad.forEach(idx => {
      if (!loadedImages[idx] && images[idx]) {
        const img = new Image();
        img.onload = () => {
          setLoadedImages(prev => ({ ...prev, [idx]: true }));
        };
        img.src = images[idx].url || images[idx];
      }
    });
  }, [currentIndex, images, loadedImages]);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
    setDragOffset(0);
  }, []);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    if (images.length <= 1) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const diff = e.clientX - dragStartX;
    setDragOffset(diff);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // If dragged more than 100px, change slide
    if (Math.abs(dragOffset) > 100) {
      if (dragOffset > 0) goToPrev();
      else goToNext();
    }
    setDragOffset(0);
  };

  // Touch handlers for mobile
  const [touchStart, setTouchStart] = useState(null);
  
  const handleTouchStart = (e) => {
    if (images.length <= 1) return;
    setTouchStart(e.touches[0].clientX);
    setIsDragging(true);
    setDragStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!touchStart || !isDragging) return;
    const diff = e.touches[0].clientX - dragStartX;
    setDragOffset(diff);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    setIsDragging(false);
    
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) goToNext();
      else goToPrev();
    }
    
    setTouchStart(null);
    setDragOffset(0);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrev, goToNext]);

  if (!images.length) {
    return (
      <div className={`bg-slate-100 rounded-xl flex items-center justify-center ${className}`} style={{ aspectRatio }}>
        <p className="text-slate-400">No images</p>
      </div>
    );
  }

  const getImageUrl = (img) => img?.url || img?.thumbnail || img;
  const getThumbnailUrl = (img) => img?.thumbnail || img?.url || img;

  return (
    <div className={`relative ${className}`} data-testid="image-gallery-slider">
      <div className={`flex ${thumbnailPosition === 'left' ? 'flex-row-reverse' : thumbnailPosition === 'right' ? 'flex-row' : 'flex-col'} gap-2`}>
        {/* Main Slider */}
        <div className="flex-1">
          <div 
            ref={sliderRef}
            className="relative overflow-hidden rounded-xl bg-slate-100"
            style={{ aspectRatio }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Slides Container */}
            <div 
              className="flex h-full transition-transform duration-300 ease-out"
              style={{
                transform: `translateX(calc(-${currentIndex * 100}% + ${isDragging ? dragOffset : 0}px))`,
                transition: isDragging ? 'none' : 'transform 0.3s ease-out'
              }}
            >
              {images.map((img, idx) => (
                <div 
                  key={idx}
                  className="flex-shrink-0 w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing"
                  onClick={() => !isDragging && onImageClick?.(idx)}
                >
                  <img
                    src={getImageUrl(img)}
                    alt={img?.alt || `Image ${idx + 1}`}
                    className="max-w-full max-h-full object-contain select-none"
                    loading={idx === currentIndex || Math.abs(idx - currentIndex) === 1 ? 'eager' : 'lazy'}
                    draggable={false}
                  />
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            {showArrows && images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white shadow-lg rounded-full z-10"
                  onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                  data-testid="slider-prev"
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white shadow-lg rounded-full z-10"
                  onClick={(e) => { e.stopPropagation(); goToNext(); }}
                  data-testid="slider-next"
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </>
            )}

            {/* Control Buttons (Fullscreen, Auto-play) */}
            <div className="absolute top-2 right-2 flex gap-2 z-10">
              {images.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-8 h-8 bg-white/80 hover:bg-white shadow rounded-full"
                  onClick={(e) => { e.stopPropagation(); setIsPlaying(!isPlaying); }}
                  title={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
                >
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </Button>
              )}
              {enableFullscreen && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-8 h-8 bg-white/80 hover:bg-white shadow rounded-full"
                  onClick={(e) => { e.stopPropagation(); setFullscreenOpen(true); }}
                  title="Fullscreen"
                  data-testid="fullscreen-btn"
                >
                  <Maximize2 className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Slide Counter */}
            {images.length > 1 && (
              <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 text-white text-xs rounded-full z-10">
                {currentIndex + 1} / {images.length}
              </div>
            )}
          </div>

          {/* Dots Indicator */}
          {showDots && images.length > 1 && images.length <= 10 && (
            <div className="flex justify-center gap-2 mt-3">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToSlide(idx)}
                  className={`transition-all rounded-full ${
                    idx === currentIndex 
                      ? 'w-6 h-2 bg-sky-500' 
                      : 'w-2 h-2 bg-slate-300 hover:bg-slate-400'
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Thumbnails */}
        {showThumbnails && images.length > 1 && (
          <div className={`
            ${thumbnailPosition === 'bottom' ? 'flex gap-2 overflow-x-auto py-2 px-1' : ''}
            ${['left', 'right'].includes(thumbnailPosition) ? 'flex flex-col gap-2 overflow-y-auto w-20 py-1' : ''}
          `}>
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => goToSlide(idx)}
                className={`flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                  thumbnailPosition === 'bottom' ? 'w-16 h-16' : 'w-16 h-16'
                } ${
                  idx === currentIndex 
                    ? 'border-sky-500 ring-2 ring-sky-500/30' 
                    : 'border-transparent opacity-60 hover:opacity-100'
                }`}
                data-testid={`thumbnail-${idx}`}
              >
                <img
                  src={getThumbnailUrl(img)}
                  alt={`Thumbnail ${idx + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      <FullscreenGallery
        open={fullscreenOpen}
        images={images}
        initialIndex={currentIndex}
        onClose={() => setFullscreenOpen(false)}
        enableZoom={enableZoom}
      />
    </div>
  );
};

// Fullscreen Gallery with Zoom
const FullscreenGallery = ({ open, images, initialIndex = 0, onClose, enableZoom = true }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Reset when opening
  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [open, initialIndex]);

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.5, 4));
  const handleZoomOut = () => {
    const newZoom = Math.max(zoom - 0.5, 1);
    setZoom(newZoom);
    if (newZoom === 1) setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  // Touch handlers
  const [touchStart, setTouchStart] = useState(null);
  
  const handleTouchStart = (e) => {
    if (zoom === 1) {
      setTouchStart(e.touches[0].clientX);
    }
  };

  const handleTouchEnd = (e) => {
    if (!touchStart || zoom > 1) return;
    
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) goToNext();
      else goToPrev();
    }
    
    setTouchStart(null);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-') handleZoomOut();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, zoom]);

  const getImageUrl = (img) => img?.url || img?.thumbnail || img;

  if (!images.length) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0 bg-black border-0">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-white/10 bg-black/50">
            <span className="text-white/70 text-sm">
              {currentIndex + 1} / {images.length}
            </span>
            <div className="flex items-center gap-2">
              {enableZoom && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20 w-9 h-9"
                    onClick={handleZoomOut}
                    disabled={zoom <= 1}
                    data-testid="zoom-out"
                  >
                    <ZoomOut className="w-5 h-5" />
                  </Button>
                  <span className="text-white/70 text-sm w-14 text-center">{Math.round(zoom * 100)}%</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20 w-9 h-9"
                    onClick={handleZoomIn}
                    disabled={zoom >= 4}
                    data-testid="zoom-in"
                  >
                    <ZoomIn className="w-5 h-5" />
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20 w-9 h-9 ml-2"
                onClick={onClose}
                data-testid="close-fullscreen"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Image Area */}
          <div 
            className="flex-1 relative overflow-hidden cursor-grab active:cursor-grabbing"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              className="absolute inset-0 flex items-center justify-center transition-transform duration-200"
              style={{
                transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`
              }}
            >
              <img
                src={getImageUrl(images[currentIndex])}
                alt={`Image ${currentIndex + 1}`}
                className="max-w-full max-h-full object-contain select-none"
                draggable={false}
              />
            </div>

            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 text-white rounded-full"
                  onClick={goToPrev}
                >
                  <ChevronLeft className="w-8 h-8" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 text-white rounded-full"
                  onClick={goToNext}
                >
                  <ChevronRight className="w-8 h-8" />
                </Button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="p-3 border-t border-white/10 bg-black/50">
              <div className="flex gap-2 overflow-x-auto justify-center pb-1">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setCurrentIndex(idx);
                      setZoom(1);
                      setPosition({ x: 0, y: 0 });
                    }}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === currentIndex 
                        ? 'border-sky-500 scale-110' 
                        : 'border-transparent opacity-50 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={img?.thumbnail || getImageUrl(img)}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageGallerySlider;
